import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { AccountService } from '../../../../accounts/account.service';
import { PartialDocument, ReSubmitToZatcaInput } from '../../../types';
import {
  InvoiceDtoWithValidity,
  MappedBillingDocument,
  MappedBillingDocumentWithPaymentReceiptData,
  MapAdditionalIdentifier,
  RetryAndResubmissionPayload,
} from '../../../../accounts/types';
import { CustomToastService } from '../../../services/custom-toast.service';

@Component({
  selector: 'rwa-re-submit-to-zatca',
  templateUrl: './re-submit-to-zatca.component.html',
  styleUrl: './re-submit-to-zatca.component.scss',
})
export class ReSubmitToZatcaComponent implements OnChanges {
  constructor(
    private readonly accountService: AccountService,
    private customToastService: CustomToastService,
  ) {}

  @Input({ required: true }) reSubmitToZatcaInput: ReSubmitToZatcaInput;

  loading = false;

  invoice: MappedBillingDocument;

  showSpinner = false;

  isPrintEnabled = false;

  reSubmitLabel: string = 'Fix and Re-submit Invoice';

  documentType: string;

  completed = false;

  zatcaErrors: string[];

  billingInfoDialogVisible: boolean = false;

  activeIndex = 0;

  subscriptionId: string;

  email: string;

  invoiceData = signal<InvoiceDtoWithValidity>({
    isValid: false,
    vatNumber: '',
    city: '',
    countryCode: '',
    country: '',
    streetName: '',
    postalCode: '',
    districtName: '',
    companyName: '',
    additionalIdentifier: '',
    additionalIdentifierNumber: '',
    buildingNumber: '',
    additionalNumber: '',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() closeDialog = new EventEmitter<'no-fetch' | undefined>();

  @Output() routeToAccountDetails = new EventEmitter<string | undefined>();

  @Output() print = new EventEmitter<MappedBillingDocument>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.reSubmitToZatcaInput &&
      changes.reSubmitToZatcaInput.currentValue.dialogVisible === true
    ) {
      this.activeIndex = 0;
      this.completed = false;
      this.showErrorsIfAny();
      this.setResubmitLabel();

      this.showErrorsIfAny();
      this.setResubmitLabel();
    }
  }

  handlePrint(): void {
    this.closeDialog.emit('no-fetch');
    if (this.reSubmitToZatcaInput.wholeInvoice)
      this.print.emit(
        this.reSubmitToZatcaInput.document as MappedBillingDocument,
      );
  }

  handleRouteToAccountDetails(): void {
    this.closeDialog.emit();
    if (this.reSubmitToZatcaInput.wholeInvoice)
      this.routeToAccountDetails.emit();
    else
      this.routeToAccountDetails.emit(
        (this.reSubmitToZatcaInput.document as PartialDocument).email,
      );
  }

  setResubmitLabel(): void {
    if (this.reSubmitToZatcaInput.wholeInvoice)
      this.invoice = {
        ...(this.reSubmitToZatcaInput.document as MappedBillingDocument),
      };
    const type = this.reSubmitToZatcaInput.wholeInvoice
      ? (this.reSubmitToZatcaInput.document as MappedBillingDocument).type
      : (this.reSubmitToZatcaInput.document as PartialDocument).type;
    this.documentType = type === 'invoice' ? 'Invoice' : 'Credit Note';
    this.reSubmitLabel =
      this.reSubmitToZatcaInput.document.type === 'invoice'
        ? 'Fix and Re-submit Invoice'
        : 'Re-submit Credit Note';
  }

  reSubmitToZatca(): void {
    let payload: RetryAndResubmissionPayload = {
      id: this.reSubmitToZatcaInput.document.id,
      type: this.reSubmitToZatcaInput.document.type,
    };

    if (this.reSubmitToZatcaInput.document.type === 'invoice') {
      payload = { ...payload, invoiceData: this.invoiceData() };
    }

    this.loading = true;

    if (payload.type === 'invoice') {
      this.accountService.updateInvoiceBilling(payload).subscribe({
        next: () => {
          // If successful, call retryInvoicesOrCreditNotes
          this.handleRetryAndResubmit(payload);
        },
        error: (err) => {
          this.customToastService.error(
            err.error.message || 'Failed to update invoice billing.',
          );
          this.completed = false;
          this.loading = false;
          this.closeDialog.emit();
        },
      });
    } else {
      this.handleRetryAndResubmit(payload);
    }
  }

  handleRetryAndResubmit(payload: RetryAndResubmissionPayload): void {
    this.accountService.retryInvoicesOrCreditNotes(payload).subscribe({
      next: () => {
        this.loading = false;
        this.completed = true;
      },
      complete: () => {
        this.loading = false;
        this.completed = true;
      },
      error: (err) => {
        this.customToastService.error(err.error.message);
        this.completed = false;
        this.loading = false;
        this.closeDialog.emit();
      },
    });
  }

  moveToFix(): void {
    if (this.reSubmitToZatcaInput.document.type === 'invoice') {
      this.activeIndex = 1;
    } else {
      this.reSubmitToZatca();
    }
  }

  back(): void {
    this.activeIndex = 0;
  }

  public get isSecondStepDisabled(): boolean {
    return !this.invoiceData().isValid;
  }

  public get printLabel(): string {
    return `Print ${this.documentType || 'Document'}`;
  }

  showErrorsIfAny(): void {
    this.zatcaErrors = this.reSubmitToZatcaInput.document.zatcaError?.map(
      (message) => message.message,
    );
  }

  close(): void {
    this.closeDialog.emit();
  }
}
